// src/services/SupervisorDashboardService.js
import api from './api';
import defesaService from './defesaService';
import avaliacaoService from './avaliacaoService';

const getSupervisaoData = async () => {
  try {
    console.log('🔍 SupervisorDashboardService: Iniciando busca de dados...');

    const promises = [];
    const data = {
      propostas: [],
      projetos: [],
      monografias: [],
      defesas: [],
      avaliacoes: []
    };

    // 1. Buscar Propostas
    promises.push(
      api.get('/propostas')
        .then(response => {
          console.log('✅ Propostas encontradas:', response.data?.length || 0);
          data.propostas = response.data || [];
        })
        .catch(error => {
          console.log('⚠️ Erro ao buscar propostas:', error.response?.status);
          // Se erro 404, apenas ignora (não há propostas)
          if (error.response?.status !== 404) {
            console.error('❌ Erro crítico em propostas:', error);
          }
        })
    );

    // 2. Buscar Projetos
    promises.push(
      api.get('/projetos')
        .then(response => {
          console.log('✅ Projetos encontrados:', response.data?.length || 0);
          data.projetos = response.data || [];
        })
        .catch(error => {
          console.log('⚠️ Erro ao buscar projetos:', error.response?.status);
          if (error.response?.status !== 404) {
            console.error('❌ Erro crítico em projetos:', error);
          }
        })
    );

    // 3. Buscar Monografias
    promises.push(
      api.get('/monografias')
        .then(response => {
          console.log('✅ Monografias encontradas:', response.data?.length || 0);
          data.monografias = response.data || [];
        })
        .catch(error => {
          console.log('⚠️ Erro ao buscar monografias:', error.response?.status);
          if (error.response?.status !== 404) {
            console.error('❌ Erro crítico em monografias:', error);
          }
        })
    );

    // 4. Buscar Defesas (usando o serviço existente)
    promises.push(
      defesaService.listarDefesasDoAvaliador()
        .then(response => {
          console.log('✅ Defesas encontradas:', response.data?.length || 0);
          data.defesas = response.data || [];
        })
        .catch(error => {
          console.log('⚠️ Erro ao buscar defesas:', error.response?.status);
          if (error.response?.status !== 404) {
            console.error('❌ Erro crítico em defesas:', error);
          }
        })
    );

    // 5. Buscar Avaliações (usando o serviço existente)
    promises.push(
      avaliacaoService.listarAvaliacoes()
        .then(response => {
          console.log('✅ Avaliações encontradas:', response.data?.length || 0);
          data.avaliacoes = response.data || [];
        })
        .catch(error => {
          console.log('⚠️ Erro ao buscar avaliações:', error.response?.status);
          if (error.response?.status !== 404) {
            console.error('❌ Erro crítico em avaliações:', error);
          }
        })
    );

    // Aguardar todas as requisições
    await Promise.all(promises);

    console.log('📊 Dados finais coletados:', {
      propostas: data.propostas.length,
      projetos: data.projetos.length,
      monografias: data.monografias.length,
      defesas: data.defesas.length,
      avaliacoes: data.avaliacoes.length
    });

    return data;

  } catch (error) {
    console.error('❌ Erro crítico em getSupervisaoData:', error);
    throw error;
  }
};

// Função alternativa para buscar dados específicos do supervisor
const getSupervisaoDataBySupervisor = async (supervisorId) => {
  try {
    console.log(`🔍 Buscando dados para supervisor ID: ${supervisorId}`);

    const promises = [];
    const data = {
      propostas: [],
      projetos: [],
      monografias: [],
      defesas: [],
      avaliacoes: []
    };

    // Buscar dados filtrados por supervisor
    promises.push(
      api.get(`/propostas/supervisor/${supervisorId}`)
        .then(response => {
          data.propostas = response.data || [];
        })
        .catch(error => {
          if (error.response?.status !== 404) {
            console.error('Erro ao buscar propostas do supervisor:', error);
          }
        })
    );

    promises.push(
      api.get(`/projetos/supervisor/${supervisorId}`)
        .then(response => {
          data.projetos = response.data || [];
        })
        .catch(error => {
          if (error.response?.status !== 404) {
            console.error('Erro ao buscar projetos do supervisor:', error);
          }
        })
    );

    promises.push(
      api.get(`/monografias/supervisor/${supervisorId}`)
        .then(response => {
          data.monografias = response.data || [];
        })
        .catch(error => {
          if (error.response?.status !== 404) {
            console.error('Erro ao buscar monografias do supervisor:', error);
          }
        })
    );

    // Para defesas e avaliações, usar os serviços existentes
    promises.push(
      defesaService.listarDefesasDoAvaliador()
        .then(response => {
          data.defesas = response.data || [];
        })
        .catch(error => {
          if (error.response?.status !== 404) {
            console.error('Erro ao buscar defesas:', error);
          }
        })
    );

    promises.push(
      avaliacaoService.listarAvaliacoes()
        .then(response => {
          data.avaliacoes = response.data || [];
        })
        .catch(error => {
          if (error.response?.status !== 404) {
            console.error('Erro ao buscar avaliações:', error);
          }
        })
    );

    await Promise.all(promises);

    return data;

  } catch (error) {
    console.error('Erro em getSupervisaoDataBySupervisor:', error);
    throw error;
  }
};

// Funções individuais para debug
const getPropostas = async () => {
  try {
    const response = await api.get('/propostas');
    console.log('Debug - Propostas raw:', response.data);
    return response.data;
  } catch (error) {
    console.error('Debug - Erro em propostas:', error);
    return [];
  }
};

const getMonografias = async () => {
  try {
    const response = await api.get('/monografias');
    console.log('Debug - Monografias raw:', response.data);
    return response.data;
  } catch (error) {
    console.error('Debug - Erro em monografias:', error);
    return [];
  }
};

const testAllEndpoints = async () => {
  console.log('🧪 Testando todos os endpoints...');
  
  const endpoints = [
    { name: 'Propostas', url: '/propostas' },
    { name: 'Projetos', url: '/projetos' },
    { name: 'Monografias', url: '/monografias' },
    { name: 'Defesas', url: '/defesas' },
    { name: 'Avaliações', url: '/avaliacoes' },
  ];

  for (const endpoint of endpoints) {
    try {
      const response = await api.get(endpoint.url);
      console.log(`✅ ${endpoint.name}:`, response.data?.length || 0, 'itens');
    } catch (error) {
      console.log(`❌ ${endpoint.name}:`, error.response?.status || 'Erro de conexão');
    }
  }
};

const SupervisorDashboardService = {
  getSupervisaoData,
  getSupervisaoDataBySupervisor,
  getPropostas,
  getMonografias,
  testAllEndpoints
};

export default SupervisorDashboardService;